import { FormControl, UntypedFormGroup } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import LayerUtils from "@vision/core-map-api/src/utils/LayerUtils";
import { Inject } from "@angular/core";
import { MatDialog, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BaseViewerService } from "../../viewer-components/services/internal-services/viewer-services/base-viewer.service";
import { IDialogData } from "../../viewer-components/interfaces/i-dialog-data";
import { GeoJSONDatabaseService } from "../../viewer-components/services/internal-services/geojson-database.service";
import { ViewerConfigurationService } from "../../viewer-components/services/viewer-configuration.service";
import { Viewerlayer } from "../../viewer-components/model/viewerlayer";
import { ILayerStyleVector } from "../../viewer-components/interfaces/i-layer-style-vector";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import { GoogleAnalyticsService } from "../../shared/services/google-analytics.service";

export abstract class WfsLayerResultsDialogComponent {
  isAuthRequired: boolean = false;

  hide = true;

  useBbox: boolean = false;

  isLoading = true;

  features: any[];

  format: any;

  url: string;

  username: string = "";

  password: string = "";

  viewerService: BaseViewerService;

  formatPercentLabel = (value: number) => `${value}%`;

  newLayerForm = new UntypedFormGroup({
    newLayerName: new FormControl(""),
    newLayerOpacity: new FormControl(100),
  });

  protected constructor(
    protected http: HttpClient,
    protected viewerConfigurationService: ViewerConfigurationService,
    protected geoJSONDatabaseService: GeoJSONDatabaseService,
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: IDialogData,
  ) {
    this.url = data.url;
    this.username = data.username;
    this.password = data.password;
    this.isAuthRequired = data.isAuthRequired;
    this.viewerService = data.viewerService;
    this.useBbox = data.useBbox;
    this.features = data.layers;
    this.format = data.wfsFormat;
    if (this.features.length) {
      for (let index = 0; index < this.features.length; index++) {
        this.newLayerForm.addControl(`newLayerName-${index}`, new FormControl(`${this.features[index].Title}`));
      }
    }
  }

  changeCheckFeature(checked: boolean, feature: any) {
    feature.checked = checked;
  }

  changeFeatureName(event, index) {
    event.preventDefault();
    const featureId = `newLayerName-${index}`;
    this.features[index].Title = this.newLayerForm.get(featureId).value;
  }

  anyChecked(): boolean {
    if (this.features) {
      return this.features.some((feature) => feature.checked !== undefined && feature.checked);
    }

    return false;
  }

  editFeatureName(feature: any) {
    if (feature.edit) {
      feature.edit = false;
    } else {
      feature.edit = true;
    }
  }

  addWFSLayers() {
    let { url } = this;
    url = LayerUtils.removeParam("request", url);

    const layerNames = [];
    let extraParams;
    if (this.isAuthRequired && this.username.length > 0 && this.password.length > 0) {
      extraParams = {
        URL_ORIGIN: url,
        USERNAME: this.username,
        PASSWORD: this.password,
      };
    }
    this.features
      .filter((layer) => layer.checked)
      .map((layer) => {
        layerNames.push(layer.Name);
        return layer;
      });
    const layersText = layerNames.join(",");
    let name = this.newLayerForm.get("newLayerName").value;
    let opacity = this.newLayerForm.get("newLayerOpacity").value;
    if (name.length === 0) {
      name = "Custom WFS Layer";
    }
    if (opacity === "") {
      opacity = 1;
    } else {
      opacity /= 100;
    }
    const wfsStyle: ILayerStyleVector = {
      defaultSetting: {
        opacity: opacity.toString(),
        colorFill: `rgba(49,159,211,${opacity})`,
        colorFillOpacity: opacity.toString(),
        colorOutline: `rgba(49,159,211,1)`,
        colorOutlineOpacity: "1",
        outlineWidth: "1",
      },
    };
    const layer = {
      source: ViewerConstants.SOURCE_NAME.WFS,
      url,
      extraParams,
      removable: true,
      owner: true,
      editable: true,
      layer: layersText,
      isBase: false,
      visible: true,
      realId: `${name.replace(/ /g, "")}_${LayerUtils.layerGuid()}`,
      id: `${name}_${LayerUtils.layerGuid()}`,
      name,
      panelInclude: true,
      style: JSON.stringify(wfsStyle),
      order: ViewerConstants.USER_LAYER_BASE_ZINDEX,
      userId: this.viewerConfigurationService.getViewerConfiguration().userId,
      format: this.format,
      useBbox: this.useBbox,
    };

    const layerToSave = LayerUtils.processLayer(layer) as unknown as Viewerlayer;
    this.saveLayer(layerToSave);
    this.matDialog.closeAll();
  }

  setBbox(event) {
    this.useBbox = event.checked;
  }

  enableFormControl(event, layerId) {
    event.preventDefault();
    this.newLayerForm.controls[`newLayerName-${layerId}`].enable();
  }

  abstract saveLayer(layer: Viewerlayer);
}
